/**
 * Compatibility shim: maps next-i18next APIs to work during the migration to next-intl.
 *
 * This allows the ~200 component files that import `useTranslation` from 'next-i18next'
 * to continue working without code changes.
 *
 * The tsconfig path alias + webpack alias maps 'next-i18next' → this file.
 *
 * KEY DESIGN DECISION: The shim does NOT use next-intl's `useTranslations` for Pages
 * Router components. Instead, it stores the original flat translation messages in a React
 * context (FlatMessagesContext) and performs direct flat-key lookups. This avoids the
 * dot-notation conflict where a key like "nav.utility.account" is both a leaf value AND
 * a parent of "nav.utility.account.menu" — next-intl's nested structure can't represent both.
 *
 * For new App Router pages, use next-intl's `useTranslations` directly (not this shim).
 * AppRouterContext is set to true by AppProviders; calling useTranslation inside it
 * logs a dev warning so unmigrated components are immediately visible.
 */
import React, { createContext, useContext } from 'react';
import { useLocale } from '@hooks/useLocale';
import type { TFunction } from 'i18next';

// Context holding the flat (non-nested) messages keyed by namespace.
// Shape: { common: { "nav.utility.account": "Account", ... }, pdp: { ... } }
type FlatMessages = Record<string, Record<string, string>>;
export const FlatMessagesContext = createContext<FlatMessages>({});

/**
 * Set to true by AppProviders (App Router). When true, any call to useTranslation
 * (the next-i18next shim) logs a dev warning — the component needs to migrate to
 * useTranslationWithSelfFallback.
 */
export const AppRouterContext = createContext<boolean>(false);

type TransComponents = Record<string, React.ReactElement>;

function renderRichText(
  value: string,
  components?: TransComponents
): React.ReactNode {
  if (!components || !value.includes('<')) return value;

  const tokenRegex = /<\/?([A-Za-z][\w-]*)>/g;
  const root: { tag: string | null; children: React.ReactNode[] } = {
    tag: null,
    children: [],
  };
  const stack = [root];
  let lastIndex = 0;
  let keyCounter = 0;

  const pushText = (text: string) => {
    if (text) {
      stack[stack.length - 1].children.push(text);
    }
  };

  let match: RegExpExecArray | null;
  while ((match = tokenRegex.exec(value)) !== null) {
    const [rawToken, tag] = match;
    const isClosing = rawToken.startsWith('</');
    const start = match.index;

    pushText(value.slice(lastIndex, start));
    lastIndex = start + rawToken.length;

    if (!isClosing) {
      stack.push({ tag, children: [] });
      continue;
    }

    if (stack.length === 1 || stack[stack.length - 1].tag !== tag) {
      pushText(rawToken);
      continue;
    }

    const currentNode = stack.pop();
    if (!currentNode) continue;

    const parentNode = stack[stack.length - 1];
    const component = components[tag];

    if (!component) {
      parentNode.children.push(
        `<${tag}>`,
        ...currentNode.children,
        `</${tag}>`
      );
      continue;
    }

    const nextKey = keyCounter;
    keyCounter += 1;
    parentNode.children.push(
      React.cloneElement(
        component,
        { key: `trans-${nextKey}` },
        ...currentNode.children
      )
    );
  }

  pushText(value.slice(lastIndex));

  while (stack.length > 1) {
    const danglingNode = stack.pop();
    if (!danglingNode) continue;

    stack[stack.length - 1].children.push(
      `<${danglingNode.tag}>`,
      ...danglingNode.children
    );
  }

  return root.children;
}

export function useTranslation(namespace?: string | string[]) {
  const ns = Array.isArray(namespace) ? namespace[0] : namespace || 'common';
  const flatMessages = useContext(FlatMessagesContext);
  const nsMessages = flatMessages[ns] || {};
  const isAppRouter = useContext(AppRouterContext);

  if (process.env.NODE_ENV === 'development' && isAppRouter) {
    const componentName = (() => {
      try {
        const frames = (new Error().stack ?? '').split('\n');
        // Skip frames that belong to the shim itself or to React/Next internals;
        // the first remaining frame is the component that called useTranslation().
        // Find the first frame outside the shim that names a PascalCase component.
        const callerFrame = frames.find(
          (f) => !f.includes('next-i18next-shim') && /at\s+([A-Z]\w+)/.test(f)
        );
        return callerFrame?.match(/at\s+([A-Z]\w+)/)?.[1] ?? 'unknown';
      } catch {
        return 'unknown';
      }
    })();

    console.warn(
      `[i18n] useTranslation('${ns}') called inside App Router in <${componentName}>. ` +
        `Migrate this component to useTranslationWithSelfFallback('${ns}').`
    );
  }

  const locale = useLocale();

  const t = ((
    key: string,
    defaultValueOrOptions?: string | Record<string, unknown>,
    maybeOptions?: Record<string, unknown>
  ) => {
    // Normalize i18next's overloaded signatures:
    //   t(key) | t(key, defaultString) | t(key, options) | t(key, defaultString, options)
    const options: Record<string, unknown> | undefined =
      typeof defaultValueOrOptions === 'object' &&
      defaultValueOrOptions !== null
        ? defaultValueOrOptions
        : maybeOptions;
    const stringDefault =
      typeof defaultValueOrOptions === 'string'
        ? defaultValueOrOptions
        : undefined;
    const optionsDefault =
      options && typeof options.defaultValue === 'string'
        ? (options.defaultValue as string)
        : undefined;
    const defaultValue = stringDefault ?? optionsDefault;

    let value: string | undefined;
    const colonIndex = key.indexOf(':');
    if (colonIndex !== -1) {
      const keyNs = key.slice(0, colonIndex);
      const keyPath = key.slice(colonIndex + 1);
      value = flatMessages[keyNs]?.[keyPath];
    } else if (options && typeof options.ns === 'string') {
      value = flatMessages[options.ns]?.[key];
    } else {
      value = nsMessages[key];
    }
    if (value === undefined) return defaultValue ?? key;

    // Handle i18next-style {{var}} interpolation
    if (options && typeof value === 'string') {
      value = value.replace(/\{\{(\w+)\}\}/g, (_, varName) => {
        return options[varName] !== undefined
          ? String(options[varName])
          : `{{${varName}}}`;
      });
    }

    return value;
  }) as unknown as TFunction;

  return {
    t,
    i18n: {
      language: locale,
      changeLanguage: () => Promise.resolve(),
    },
    ready: true,
  };
}

export function Trans({
  i18nKey,
  children,
  values,
  components,
}: {
  i18nKey?: string;
  children?: React.ReactNode;
  values?: Record<string, unknown>;
  components?: TransComponents;
  [key: string]: unknown;
}): React.ReactElement {
  const { t } = useTranslation();
  const translated = i18nKey ? t(i18nKey, values) : undefined;
  const content =
    children ??
    (typeof translated === 'string'
      ? renderRichText(translated, components)
      : translated ?? '');

  return React.createElement(React.Fragment, null, content);
}

// Re-export TFunction type for files that import it
export type { TFunction };

// No-op: the real wrapping is now done by NextIntlClientProvider in _app.tsx
export function appWithTranslation<T>(Component: T): T {
  return Component;
}

// Replaces next-i18next's serverSideTranslations.
// Returns both:
//   - `messages`: nested format for NextIntlClientProvider (App Router compat)
//   - `flatMessages`: original flat format for the shim's FlatMessagesContext
// Pages spread this into props: `...(await serverSideTranslations(locale, ns))`
export async function serverSideTranslations(
  locale: string,
  namespaces?: string[]
) {
  const { getMessages, getFlatMessages } = await import('./getMessages');
  const nsList = namespaces || ['common'];
  const [messages, flatMessages] = await Promise.all([
    getMessages(locale, nsList),
    getFlatMessages(locale, nsList),
  ]);
  return { messages, flatMessages };
}
